import {
  BasicProduct,
  CommaSeparatedProductObject,
  OrderItem,
  OrderOption,
  OrderOptionEnum,
  PaymentType,
  PickupAddress,
  ShipmentFeeType,
  SingleTicketResponse,
  TicketField,
  TicketFormResponse,
} from '@/types';
import { formatWithThousandSeparator } from '.';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { issueFieldList, TICKET_ENTITY_CONFIG } from '@/constants';
import { ROUTE } from '@/config';
import { generatePath } from 'react-router-dom';

export function humanizeKey(key: string): string {
  const words = key.split('_') || key.split('-');

  const capitalizedWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return capitalizedWords.join(' ');
}

//
export function commaSeparatedObjectToArray(obj: {
  product_title: string;
  product_price: string;
  product_quantity: string;
}) {
  // If all inputs are empty, return empty array
  if (!obj.product_title && !obj.product_price && !obj.product_quantity) {
    return [];
  }

  const titles =
    obj.product_title
      ?.split(',')
      .map((t: string) => t.trim())
      .filter(Boolean) ?? [];
  if (titles.length === 0) return [];

  // Smart parsing: detect corrupted data where values were concatenated without commas.
  // e.g. product_price="00045000" for 4 products (should be "0,0,0,45000")
  // or product_quantity="1111" for 4 products (should be "1,1,1,1")
  const parseField = (
    raw: string | undefined,
    titleCount: number,
    defaultVal: string,
  ): string[] => {
    if (!raw || raw.trim() === '') return Array(titleCount).fill(defaultVal);

    const parts = raw.split(',').map((v: string) => v.trim());

    // If we have the right count, use as-is
    if (parts.length === titleCount) return parts;

    // If we have more parts than titles, truncate
    if (parts.length > titleCount) return parts.slice(0, titleCount);

    // If single value and multiple titles: check if it looks like concatenated digits
    // e.g. "1111" for 4 titles likely means "1,1,1,1" not "1111 units"
    if (parts.length === 1 && titleCount > 1) {
      const val = parts[0];

      // For quantities: if all chars are the same digit and length matches title count,
      // split into individual digits (e.g. "1111" for 4 titles → [1,1,1,1])
      if (defaultVal === '1' && /^\d+$/.test(val) && val.length === titleCount) {
        return val.split('');
      }

      // For prices: if starts with zeros and length suggests concatenation
      // e.g. "00045000" for 4 titles → likely "0,0,0,45000"
      if (defaultVal === '0' && /^0+\d/.test(val) && val.startsWith('0')) {
        const leadingZeros = val.match(/^(0+)/)?.[1]?.length || 0;
        if (leadingZeros === titleCount - 1) {
          const zeros = Array(leadingZeros).fill('0');
          const remainder = val.substring(leadingZeros);
          return [...zeros, remainder];
        }
      }

      // Fallback: assign value to first, pad rest with defaults
      const result = [val];
      while (result.length < titleCount) result.push(defaultVal);
      return result;
    }

    // Pad shorter arrays
    while (parts.length < titleCount) parts.push(defaultVal);
    return parts;
  };

  const prices = parseField(obj.product_price, titles.length, '0');
  const quantities = parseField(obj.product_quantity, titles.length, '1');

  return titles.map((title: string, index: number) => ({
    title,
    price: prices[index] || '0',
    quantity: Number(quantities[index]) || 1,
  }));
}
//
export function arrayToCommaSeparatedObject(array: BasicProduct[]): CommaSeparatedProductObject {
  const productTitles = array.map((item) => item.title).join(', ');
  const productPrices = array.map((item) => item?.price?.toString()).join(',');
  const productQuantities = array.map((item) => item?.quantity?.toString()).join(',');

  return {
    product_title: productTitles,
    product_price: productPrices,
    product_quantity: productQuantities,
  };
}

export const getOrderPickupAddress = (orderData: OrderItem): PickupAddress | undefined => {
  return (
    (orderData?.status?.title === 'new'
      ? orderData?.saved_pick_up_address
      : orderData?.pick_up?.pick_up_address) || undefined
  );
};

export const getWarehouseAddressType = (pickupAddresses?: PickupAddress[]) => {
  return pickupAddresses?.filter((item) => item.type === 'WAREHOUSE') || [];
};

export const getSidebarRouteInfo = (_pathname: string) => {
  const sidebarRoutes = new Map<string, string>([
    ['orders', 'orders'],
    ['pickups', 'shipment'],
    ['finance', 'finance'],
    ['advance-cod', 'advance_cod'],
    ['dashboard', 'overview'],
    ['analytics', 'analytics'],
    ['inbox', 'inbox'],
    ['engage', 'engage'],
    ['settings', 'settings'],
    ['ticket-center', 'ticket_center'],
    ['claims', 'claims'],
  ]);

  // Routes that should highlight a different sidebar item
  const routeAliases = new Map<string, string>([['import', 'orders']]);

  const notValidRoutes = new Set(['no-data', '400', '500', '*']);
  const pathname = _pathname.startsWith('/') ? _pathname.slice(1) : _pathname;

  if (notValidRoutes.has(pathname)) {
    return null;
  }

  const basePath = pathname.split('/')[0];

  // Check aliases first (e.g., /import -> orders)
  const aliasedPath = routeAliases.get(basePath);
  if (aliasedPath) {
    const matchedRoute = sidebarRoutes.get(aliasedPath);
    if (matchedRoute) {
      return { location: aliasedPath, name: matchedRoute };
    }
  }

  const matchedRoute = sidebarRoutes.get(basePath);

  if (matchedRoute) {
    return { location: basePath, name: matchedRoute };
  }

  return { location: '/', name: 'Unknown' };
};

export const getPageTitleWithPathAndHash = (pathname: string, hash: string) => {
  const routeInfo = getSidebarRouteInfo(pathname);

  // Check for pickups page with rto hash
  if (pathname === 'pickups' || pathname === '/pickups') {
    if (hash === '#rto') {
      return 'returns_list';
    }
  }

  // For all other cases, return the regular sidebar route name
  return routeInfo?.name || 'Unknown';
};
export const calculateCustomerWillPay = (
  paymentType: PaymentType,
  shipmentFeeType: ShipmentFeeType,
  orderValue: number,
  shipmentFee: number,
  customShipmentFee: number,
  discount: number,
  prepayment: number,
): string => {
  // PREPAID: customer already paid — owes nothing at delivery
  if (paymentType === PaymentType.PREPAID) {
    return formatWithThousandSeparator(0);
  }

  // COD: customer pays at delivery
  const shipmentFeeValue = customShipmentFee > 0 ? customShipmentFee : shipmentFee;
  const finalOrderValue = orderValue - discount - prepayment;

  if (shipmentFeeType === ShipmentFeeType.BY_CUSTOMER) {
    // COD + BY_CUSTOMER: customer pays products + shipping
    return formatWithThousandSeparator(finalOrderValue + shipmentFeeValue);
  }
  // COD + BY_MERCHANT: customer pays products only
  return formatWithThousandSeparator(finalOrderValue);
};

/**
 * Validates if the given latitude and longitude coordinates are within valid ranges.
 * Latitude must be between -90 and 90 degrees.
 * Longitude must be between -180 and 180 degrees.
 *
 * @param {string | number | undefined} lat - The latitude coordinate
 * @param {string | number | undefined} lng - The longitude coordinate
 * @returns {boolean} true if both coordinates are valid, false otherwise
 */
export const isValidLatLng = (lat?: string | number, lng?: string | number): boolean => {
  if (lat === undefined || lng === undefined) return false;

  const [latNum, lngNum] = [lat, lng].map(Number);

  return (
    !isNaN(latNum) &&
    latNum >= -90 &&
    latNum <= 90 &&
    !isNaN(lngNum) &&
    lngNum >= -180 &&
    lngNum <= 180
  );
};
//
export const scrollTop = () => {
  if (typeof window !== 'undefined' && window.scrollTo) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

export const scrollBottom = () => {
  if (typeof window !== 'undefined' && window.scrollTo) {
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    window.scrollTo({ top: scrollHeight, behavior: 'smooth' });
  }
};

/**
 * Adjusts the price based on the order type.
 * Handles both string and number prices to avoid scientific notation for large values.
 *
 * @param {number | string} price - The price to be adjusted
 * @param {OrderTypeEnum} orderType - The type of the order
 * @returns {number | string} The adjusted price (preserves type to avoid scientific notation)
 */
export const adjustPriceByOrderType = (
  price: number | string,
  orderType: OrderTypeEnum,
): number | string => {
  // For arithmetic, use parseFloat but preserve original type for return
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  const isStringInput = typeof price === 'string';

  if (orderType === OrderTypeEnum.RETURN) {
    const adjusted = numPrice === 0 ? 0 : -Math.abs(numPrice);
    return isStringInput ? String(adjusted) : adjusted;
  } else if (orderType === OrderTypeEnum.NORMAL) {
    const adjusted = Math.abs(numPrice);
    return isStringInput ? String(adjusted) : adjusted;
  }

  return price;
};

export const adjustProductListByOrderType = (
  products: BasicProduct[],
  orderType: OrderTypeEnum,
): BasicProduct[] => {
  return products.map((product) => ({
    ...product,
    // Preserve price type to avoid scientific notation for large values
    price: adjustPriceByOrderType(product.price, orderType),
  }));
};

export const getOrderTypeTextKey = (orderType: OrderTypeEnum) => {
  if (orderType === OrderTypeEnum.EXCHANGE) return 'this_order_is_marked_as_an_Exchange_order';
  else return 'this_order_is_marked_as_a_Return_order';
};

/** Short single-word label for order type (used in order detail views) */
export const getOrderTypeLabelKey = (orderType: OrderTypeEnum) => {
  switch (orderType) {
    case OrderTypeEnum.EXCHANGE:
      return 'exchange';
    case OrderTypeEnum.RETURN:
      return 'return_order';
    default:
      return 'normal';
  }
};

export const checkProductValidation = (product?: BasicProduct) => {
  return !!product?.title && !!product?.quantity && (!!product?.price || product?.price === 0);
};

export const getIssueField = (ticketData?: SingleTicketResponse) => {
  if (!ticketData) return undefined;

  const issueField = ticketData.fields?.find((field) => {
    return issueFieldList.includes(field.id) && field.value !== null;
  });
  if (!issueField) return null;

  const issueFieldOptions = ticketData.custom_fields?.find((field) => {
    return field.id === issueField.id;
  });

  if (!issueFieldOptions) return null;

  return (
    issueFieldOptions.custom_field_options?.find((option) => option.value === issueField.value) ??
    null
  );
};

export const getTicketEntityAndPageRoute = (platformCode?: string, uid?: string) => {
  if (!platformCode) return undefined;

  const key = platformCode.split('-')[0].toUpperCase();
  const config = TICKET_ENTITY_CONFIG[key];

  return config ? config(uid) : undefined;
};

// export const getCustomStatus = (formData: TicketFormResponse[]) => {
//   const custom_statuses = formData[1]?.fields.find(
//     (field): field is CustomStatusField =>
//       field.type === 'custom_status' && field.id === TicketStatusField,
//   )?.custom_statuses;
//   return custom_statuses ?? [];
// };

export const getIssueOptionsFromForm = (_currentForm?: TicketFormResponse) => {
  if (!_currentForm) return [];
  return (
    _currentForm.fields.find((field): field is TicketField => issueFieldList.includes(field.id))
      ?.custom_field_options ?? []
  );
};

export const getFormData = (currentFormId: number, formList: TicketFormResponse[]) => {
  const currentForm = formList.find((formItem) => formItem.id === currentFormId);

  return currentForm;
};

export const getOrderOptions = (orderOption?: OrderOption[]) => {
  const clueVerification =
    orderOption?.find((opt) => opt.type === OrderOptionEnum.CLUE_VERIFICATION)?.value || undefined;
  return {
    isIdChecked: orderOption?.some((opt) => opt.type === OrderOptionEnum.ID_CHECK) || false,
    isClueVerification: !!clueVerification,
    clueLink: clueVerification,
  };
};
